export type UnitSystem = 'imperial' | 'metric'
export type FieldKey = 'width' | 'depth' | 'height' | 'pitch'

export interface MaterialOption {
  id: string
  label: string
  description: string
  claddingPerSqFt: number // installed cost, USD per sq ft of wall
  roofPerSquare: number // installed cost, USD per roofing square (100 sq ft)
}

export const MATERIALS: MaterialOption[] = [
  {
    id: 'vinyl',
    label: 'Vinyl siding + asphalt shingles',
    description: 'Lowest cost, low maintenance',
    claddingPerSqFt: 6,
    roofPerSquare: 600,
  },
  {
    id: 'fiber-cement',
    label: 'Fiber cement + architectural shingles',
    description: 'Durable, paintable, fire resistant',
    claddingPerSqFt: 11,
    roofPerSquare: 700,
  },
  {
    id: 'cedar',
    label: 'Cedar cladding + cedar or composite roof',
    description: 'Warm natural look, higher upkeep',
    claddingPerSqFt: 14,
    roofPerSquare: 900,
  },
  {
    id: 'metal',
    label: 'Standing seam metal',
    description: 'Long life, modern profile',
    claddingPerSqFt: 16,
    roofPerSquare: 1200,
  },
  {
    id: 'slate',
    label: 'Slate or clay tile hanging',
    description: 'Premium, traditional heritage finish',
    claddingPerSqFt: 18,
    roofPerSquare: 1800,
  },
]

export const LIMITS: Record<FieldKey, { min: number; max: number }> = {
  width: { min: 4, max: 30 }, // feet
  depth: { min: 3, max: 20 }, // feet
  height: { min: 3, max: 12 }, // feet
  pitch: { min: 1, max: 18 }, // inches of rise per 12 inches of run
}

export const FT_PER_M = 3.28084
export const SQFT_PER_SQM = 10.7639

export interface DormerInputs {
  width: number
  depth: number
  height: number
  pitch: number
  materialId: string
}

export interface RawInputs {
  width: string
  depth: string
  height: string
  pitch: string
}

export type FieldErrors = Partial<Record<FieldKey, string>>

const LABELS: Record<FieldKey, string> = {
  width: 'Width',
  depth: 'Depth',
  height: 'Height',
  pitch: 'Roof pitch',
}

export function round(n: number, digits = 0): number {
  const f = Math.pow(10, digits)
  return Math.round(n * f) / f
}

export function validateInputs(
  raw: RawInputs,
  unit: UnitSystem,
  materialId: string,
): { values: DormerInputs | null; errors: FieldErrors } {
  const errors: FieldErrors = {}
  const parsed: Partial<Record<FieldKey, number>> = {}
  const keys: FieldKey[] = ['width', 'depth', 'height', 'pitch']

  for (const key of keys) {
    const text = raw[key].trim()
    if (text === '') {
      errors[key] = `Enter a ${LABELS[key].toLowerCase()}.`
      continue
    }
    const num = Number(text)
    if (!Number.isFinite(num)) {
      errors[key] = `${LABELS[key]} must be a number.`
      continue
    }
    const isLength = key !== 'pitch'
    const factor = isLength && unit === 'metric' ? FT_PER_M : 1
    const feetValue = num * factor
    const { min, max } = LIMITS[key]
    if (feetValue < min - 1e-9 || feetValue > max + 1e-9) {
      if (isLength && unit === 'metric') {
        errors[key] = `${LABELS[key]} must be between ${round(min / FT_PER_M, 1)} and ${round(max / FT_PER_M, 1)} m.`
      } else if (isLength) {
        errors[key] = `${LABELS[key]} must be between ${min} and ${max} ft.`
      } else {
        errors[key] = `Pitch must be between ${min} and ${max} (inches of rise per 12 in).`
      }
      continue
    }
    parsed[key] = feetValue
  }

  if (Object.keys(errors).length > 0) return { values: null, errors }

  return {
    values: {
      width: parsed.width as number,
      depth: parsed.depth as number,
      height: parsed.height as number,
      pitch: parsed.pitch as number,
      materialId,
    },
    errors,
  }
}

export interface MaterialLine {
  name: string
  detail: string
  qty: number
  unit: 'pcs' | 'sheets' | 'sqft' | 'ft'
}

export interface CostLine {
  label: string
  amount: number
}

export interface DormerResult {
  material: MaterialOption
  rise: number
  slopeLength: number
  pitchDegrees: number
  floorArea: number
  frontWallArea: number
  cheekWallArea: number
  wallArea: number
  roofArea: number
  envelopeArea: number
  materials: MaterialLine[]
  costs: CostLine[]
  subtotal: number
  contingency: number
  permits: number
  total: number
  low: number
  high: number
  costPerSqFtFloor: number
}

const STUD_SPACING_IN = 16
const WASTE = 1.1
const SHEET_AREA = 32 // 4 ft x 8 ft

export function calculateDormer(input: DormerInputs): DormerResult {
  const material = MATERIALS.find(m => m.id === input.materialId) ?? MATERIALS[1]
  const { width, depth, height, pitch } = input

  const rise = (depth * pitch) / 12
  const slopeLength = Math.sqrt(depth * depth + rise * rise)
  const pitchDegrees = (Math.atan(pitch / 12) * 180) / Math.PI

  const floorArea = width * depth
  const frontWallArea = width * height
  const cheekWallArea = 2 * depth * (height + rise / 2)
  const wallArea = frontWallArea + cheekWallArea
  const roofArea = width * (slopeLength + 0.5) // 6 in eave overhang
  const envelopeArea = wallArea + roofArea

  const wallRun = width + 2 * depth
  const studs = Math.ceil((wallRun * 12) / STUD_SPACING_IN) + 4
  const rafters = Math.ceil((width * 12) / STUD_SPACING_IN) + 1
  const sheathingSheets = Math.ceil((envelopeArea * WASTE) / SHEET_AREA)
  const drywallSheets = Math.ceil((envelopeArea * WASTE) / SHEET_AREA)
  const claddingArea = wallArea * WASTE
  const roofCoverArea = roofArea * WASTE
  const insulationArea = envelopeArea
  const windows = Math.max(1, Math.ceil(width / 5))
  const headerLength = width + 1

  const materials: MaterialLine[] = [
    { name: 'Wall studs', detail: '2x4 or 2x6 at 16 in centres, incl. corners', qty: studs, unit: 'pcs' },
    { name: 'Roof rafters', detail: `2x8 at 16 in centres, about ${round(slopeLength + 0.5, 1)} ft long`, qty: rafters, unit: 'pcs' },
    { name: 'Structural header beam', detail: 'LVL or steel, spans the roof opening', qty: round(headerLength, 1), unit: 'ft' },
    { name: 'Sheathing', detail: '4 x 8 ft sheets, walls and roof, 10% waste', qty: sheathingSheets, unit: 'sheets' },
    { name: 'Roof covering', detail: `${material.label.split(' + ').pop()}, 10% waste`, qty: round(roofCoverArea, 0), unit: 'sqft' },
    { name: 'Wall cladding', detail: `${material.label.split(' + ')[0]}, 10% waste`, qty: round(claddingArea, 0), unit: 'sqft' },
    { name: 'Insulation', detail: 'Walls and roof surface', qty: round(insulationArea, 0), unit: 'sqft' },
    { name: 'Plasterboard / drywall', detail: '4 x 8 ft sheets, interior walls and ceiling', qty: drywallSheets, unit: 'sheets' },
    { name: 'Windows', detail: 'Roughly one unit per 5 ft of width', qty: windows, unit: 'pcs' },
  ]

  const costLines: CostLine[] = [
    { label: 'Roof opening, header beam and structural work', amount: width * 200 },
    { label: 'Floor framing and subfloor', amount: floorArea * 12 },
    { label: 'Wall and roof framing with sheathing', amount: envelopeArea * 22 },
    { label: 'Roof covering and flashing', amount: (roofCoverArea / 100) * material.roofPerSquare },
    { label: 'Wall cladding', amount: claddingArea * material.claddingPerSqFt },
    { label: 'Windows (installed)', amount: windows * 750 },
    { label: 'Insulation', amount: insulationArea * 3 },
    { label: 'Drywall and interior finishing', amount: envelopeArea * 4.5 },
    { label: 'Electrical and heating extension', amount: floorArea * 18 },
  ]
  const costs = costLines.map(c => ({ ...c, amount: round(c.amount, 0) }))

  const subtotal = costs.reduce((sum, c) => sum + c.amount, 0)
  const contingency = round(subtotal * 0.1, 0)
  const permits = round(subtotal * 0.05, 0)
  const total = subtotal + contingency + permits

  return {
    material,
    rise,
    slopeLength,
    pitchDegrees,
    floorArea,
    frontWallArea,
    cheekWallArea,
    wallArea,
    roofArea,
    envelopeArea,
    materials,
    costs,
    subtotal,
    contingency,
    permits,
    total,
    low: round(total * 0.85, -1),
    high: round(total * 1.15, -1),
    costPerSqFtFloor: total / floorArea,
  }
}

export function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

export function formatNumber(n: number, digits = 0): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  }).format(n)
}

export function formatArea(sqft: number, unit: UnitSystem): string {
  if (unit === 'metric') return `${formatNumber(sqft / SQFT_PER_SQM, 1)} m\u00B2`
  return `${formatNumber(sqft, 0)} sq ft`
}

export function formatLength(ft: number, unit: UnitSystem): string {
  if (unit === 'metric') return `${formatNumber(ft / FT_PER_M, 2)} m`
  return `${formatNumber(ft, 1)} ft`
}

export function formatMaterialQty(line: MaterialLine, unit: UnitSystem): string {
  switch (line.unit) {
    case 'sqft':
      return formatArea(line.qty, unit)
    case 'ft':
      return formatLength(line.qty, unit)
    case 'sheets':
      return `${formatNumber(line.qty)} sheets`
    default:
      return `${formatNumber(line.qty)} pcs`
  }
}

export function buildResultsText(
  input: DormerInputs,
  result: DormerResult,
  unit: UnitSystem,
): string {
  const lines: string[] = []
  lines.push('Dormer Calculator estimate (www.dormercalculator.com)')
  lines.push('')
  lines.push(
    `Size: ${formatLength(input.width, unit)} wide x ${formatLength(input.depth, unit)} deep x ${formatLength(input.height, unit)} high, pitch ${round(input.pitch, 1)}/12`,
  )
  lines.push(`Material: ${result.material.label}`)
  lines.push(`Floor area: ${formatArea(result.floorArea, unit)}`)
  lines.push(`Wall area: ${formatArea(result.wallArea, unit)}`)
  lines.push(`Roof area: ${formatArea(result.roofArea, unit)}`)
  lines.push('')
  lines.push('Materials needed:')
  result.materials.forEach(m => lines.push(`- ${m.name}: ${formatMaterialQty(m, unit)}`))
  lines.push('')
  lines.push('Cost breakdown:')
  result.costs.forEach(c => lines.push(`- ${c.label}: ${formatMoney(c.amount)}`))
  lines.push(`- Contingency (10%): ${formatMoney(result.contingency)}`)
  lines.push(`- Design and permits (5%): ${formatMoney(result.permits)}`)
  lines.push('')
  lines.push(`Estimated total: ${formatMoney(result.total)} (range ${formatMoney(result.low)} to ${formatMoney(result.high)})`)
  lines.push('Planning estimate only. Get written quotes from licensed contractors.')
  return lines.join('\n')
}
